/**
 * 🗑️ MODAL SUPPRESSION DE COMPTE
 * Permet à l'utilisateur de supprimer définitivement son compte (droit RGPD)
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, AlertTriangle, Heart, X, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DeleteAccountModal = ({ isOpen, onClose, customerId, onDeleted, isDarkMode = true, heartbeatsOnly = false }) => {
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Titre et messages selon le contexte
  const title = heartbeatsOnly ? 'Supprimer mon profil Heartbeats' : 'Supprimer mon compte';
  const warningMessage = heartbeatsOnly 
    ? 'Ton profil Heartbeats sera supprimé : matches, messages, photos... Ton compte client LPE reste intact !'
    : 'Toutes tes données seront supprimées définitivement : profil, matchs, messages, points de fidélité...';

  const reasons = [
    { id: 'found_love', label: '💕 J\'ai trouvé l\'amour !' },
    { id: 'not_satisfied', label: '😕 Je ne suis pas satisfait(e)' },
    { id: 'privacy', label: '🔒 Raisons de confidentialité' },
    { id: 'too_many_notifications', label: '🔔 Trop de notifications' },
    { id: 'taking_break', label: '⏸️ Je fais une pause' },
    { id: 'other', label: '📝 Autre raison' }
  ];

  const handleDelete = async () => {
    if (confirmText !== 'SUPPRIMER') {
      setError('Tape "SUPPRIMER" pour confirmer');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/heartbeats/delete-account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId,
          reason,
          confirmText
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Erreur lors de la suppression');
      }

      setStep(3); // Confirmation
      
      // Callback après 3 secondes
      setTimeout(() => {
        if (onDeleted) onDeleted();
      }, 3000);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setStep(1);
    setConfirmText('');
    setReason('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={resetAndClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className={`w-full max-w-md rounded-2xl overflow-hidden ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}
          onClick={(e) => e.stopPropagation()}
        >
          {step === 1 && (
            <>
              {/* Header */}
              <div className="bg-gradient-to-r from-red-600 to-orange-600 p-6 text-center text-white relative">
                <button
                  onClick={resetAndClose}
                  className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center">
                  <Trash2 className="w-8 h-8" />
                </div>
                <h2 className="text-2xl font-bold mb-2">{title}</h2>
                <p className="text-white/80 text-sm">Cette action est irréversible</p>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className={`flex items-start gap-3 p-4 rounded-xl mb-6 ${isDarkMode ? 'bg-red-900/30 border border-red-500/30' : 'bg-red-50 border border-red-200'}`}>
                  <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className={`font-bold ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>
                      Attention !
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-red-200/70' : 'text-red-700'}`}>
                      {warningMessage}
                    </p>
                  </div>
                </div>

                <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Pourquoi veux-tu nous quitter ? (optionnel)
                </p>

                <div className="space-y-2 mb-6">
                  {reasons.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setReason(r.id)}
                      className={`w-full p-3 rounded-xl text-left text-sm transition-all ${
                        reason === r.id
                          ? 'bg-red-500/20 border-2 border-red-500'
                          : isDarkMode
                            ? 'bg-gray-800 border-2 border-gray-700 hover:border-gray-600'
                            : 'bg-gray-100 border-2 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={resetAndClose}
                    className="flex-1"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={() => setStep(2)}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                  >
                    Continuer
                  </Button>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              {/* Header */}
              <div className="bg-gradient-to-r from-red-600 to-red-800 p-6 text-center text-white relative">
                <button
                  onClick={resetAndClose}
                  className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Confirmation finale</h2>
                <p className="text-white/80 text-sm">Dernière étape</p>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className={`mb-4 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Pour confirmer, tape <strong className="text-red-400">SUPPRIMER</strong> ci-dessous :
                </p>

                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  placeholder="SUPPRIMER"
                  className={`w-full px-4 py-3 rounded-xl text-center text-lg font-bold mb-4 ${
                    isDarkMode 
                      ? 'bg-gray-800 border-gray-700 text-white' 
                      : 'bg-gray-100 border-gray-300 text-gray-900'
                  } border focus:outline-none focus:ring-2 focus:ring-red-500`}
                />

                {error && (
                  <p className="text-red-400 text-sm text-center mb-4">{error}</p>
                )}

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1"
                    disabled={loading}
                  >
                    Retour
                  </Button>
                  <Button
                    onClick={handleDelete}
                    disabled={loading || confirmText !== 'SUPPRIMER'}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer définitivement
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              {/* Header */}
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 text-center text-white">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center">
                  <Check className="w-8 h-8" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Compte supprimé</h2>
                <p className="text-white/80 text-sm">Merci d&apos;avoir utilisé Heartbeats</p>
              </div>

              {/* Content */}
              <div className="p-6 text-center">
                <Heart className="w-12 h-12 mx-auto mb-4 text-pink-400" />
                <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Ton compte a été supprimé avec succès.
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  On espère te revoir bientôt ! 💜
                </p>
                <p className={`text-xs mt-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Redirection automatique...
                </p>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DeleteAccountModal;
